import AlarmIcon from "@mui/icons-material/Alarm";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import HttpService from "../../../hooks/Https-services";
import useConfig from "../../../hooks/useConfig";
import { SaveOffer, SaveOffersResponse, SaveOffersSummary, Segment } from "../../../types/subscription";
import PageRoutes, {
  APIRoutes,
  CANCEL_REASONS,
  CancelReasonValue,
  SAVE_FLOW_GA_EVENTS,
} from "../../../utils/constants";
import { useSubscriptionStatus } from "../../../Common/Contex/SubscriptionStatusContex";
import {
  buildPayUrl,
  NOT_STARTED_STATUSES,
} from "../../../utils/paymentRecovery";
import NothingToCancel from "./NothingToCancel";

const FALLBACK_SUMMARY: SaveOffersSummary = {
  planLabel: "Your Plan",
  price: "",
  interval: "month",
  renewsAt: "",
  memberSince: "",
  savedClipsCount: null,
  personalizedChannelActive: null,
  seasonalResumeAt: null,
};

const REASON_LABELS: Record<CancelReasonValue, string> = {
  too_expensive: "Too expensive",
  off_season: "It's the off-season",
  not_watching: "Not watching enough",
  technical: "Technical issues",
  other: "Other",
};

const trackGaEvent = (event: string, params?: Record<string, any>) => {
  try {
    (window as any).gtag?.("event", event, params ?? {});
  } catch (_) {}
};

// The backend sends unix-seconds numbers for dates and a numeric price; tolerate
// both the raw values and pre-formatted fallback strings.
const toDate = (v: number | string) =>
  typeof v === "number" ? dayjs.unix(v) : dayjs(v);
const fmtPrice = (v: number | string) =>
  typeof v === "number" ? `$${v.toFixed(2)}` : v;

const CancelSaveFlow: React.FC = () => {
  const configData = useConfig();
  const navigate = useNavigate();

  // HTPTV-695: the app-wide status fetch. Unlike this page's own
  // getUserAccountDetails call, /subscription-status returns the subscription
  // whatever its status, including the never-started ones the gate below cares
  // about.
  const {
    subscription: liveSubscription,
    pendingPayment,
    loading: subStatusLoading,
  } = useSubscriptionStatus();

  const primary = configData?.primary;
  const tertiary = configData?.tertiary;

  const [loading, setLoading] = useState(true);
  // HTPTV-695: fetchAccountDetails runs in parallel with fetchOffers and has no
  // loading flag of its own, and it is the source of `subscriptionId`, which the
  // gate below depends on. Without this flag, a run where GetSaveOffers resolves
  // before GetUserAccountDetails (likely, since the account endpoint makes
  // several Stripe round-trips) leaves a window where `loading` is already false
  // but `subscriptionId` has not been set yet, and the gate wrongly renders the
  // blocked card over what would otherwise be a healthy save flow.
  const [accountLoading, setAccountLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [segment, setSegment] = useState<Segment>("generic");
  const [summary, setSummary] = useState<SaveOffersSummary>(FALLBACK_SUMMARY);
  const [subscriptionId, setSubscriptionId] = useState<string>("");
  const [selectedReason, setSelectedReason] = useState<CancelReasonValue | null>(null);
  const [otherComment, setOtherComment] = useState<string>("");
  const [offers, setOffers] = useState<SaveOffer[]>([]);
  const [subState, setSubState] = useState<{
    isPaused: boolean;
    pauseResumesAt: number | null;
    isCanceled: boolean;
    periodEnd: number | null;
    customerId: string | null;
  }>({ isPaused: false, pauseResumesAt: null, isCanceled: false, periodEnd: null, customerId: null });

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await HttpService.get(APIRoutes.GetSaveOffers);
        const data: SaveOffersResponse = res?.data?.data ?? res?.data;
        setSegment(data?.segment ?? "generic");
        setSummary({ ...FALLBACK_SUMMARY, ...(data?.summary ?? {}) });
        setOffers(data?.offers ?? []);
      } catch (_) {
        setSummary(FALLBACK_SUMMARY);
      } finally {
        setLoading(false);
      }
    };

    const fetchAccountDetails = async () => {
      try {
        const res = await HttpService.get(APIRoutes.GetUserAccountDetails);
        const sub = res?.data?.data?.Subscription;
        if (sub?.id) setSubscriptionId(sub.id);
        setSubState({
          isPaused: !!sub?.pause_collection,
          pauseResumesAt: sub?.pause_collection?.resumes_at ?? null,
          isCanceled: !!sub?.cancel_at_period_end,
          periodEnd: sub?.current_period_end ?? null,
          customerId: sub?.customer ?? null,
        });
        if (!summary.planLabel || summary.planLabel === "Your Plan") {
          setSummary((prev) => ({
            ...prev,
            planLabel: sub?.plan?.nickname ?? prev.planLabel,
            price: sub?.plan?.amount ? `$${(sub.plan.amount / 100).toFixed(2)}` : prev.price,
            interval: sub?.plan?.interval ?? prev.interval,
            renewsAt: sub?.current_period_end ?? prev.renewsAt,
          }));
        }
      } catch (_) {
      } finally {
        setAccountLoading(false);
      }
    };

    trackGaEvent("cancel_flow_start");
    fetchOffers();
    fetchAccountDetails();
  }, []);

  useEffect(() => {
    if (!loading) {
      trackGaEvent(SAVE_FLOW_GA_EVENTS.OFFER_SHOWN, { segment });
    }
  }, [loading, segment]);

  const handlePause = async (pauseType: "generic" | "seasonal") => {
    if (!subscriptionId) {
      toast.error("Subscription not found");
      return;
    }
    setActionLoading(pauseType);
    try {
      const url = APIRoutes.PauseSubscription.replace(":id", subscriptionId);
      await HttpService.post(`${url}?isPause=true`, { pauseType });
      trackGaEvent(
        pauseType === "seasonal"
          ? SAVE_FLOW_GA_EVENTS.SEASONAL_HOLD
          : SAVE_FLOW_GA_EVENTS.PAUSE
      );
      toast.success(
        pauseType === "seasonal"
          ? "Subscription held until the season. We'll bring you back automatically."
          : "Subscription paused. Resume anytime from your account."
      );
      navigate(PageRoutes.AccountPage);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!subscriptionId) {
      toast.error("Subscription not found");
      return;
    }
    setActionLoading("cancel");
    try {
      await HttpService.post(APIRoutes.ChangePlanDetails, {
        subscriptionId,
        cancel_at_period_end: true,
        ...(selectedReason ? { cancelReason: selectedReason } : {}),
        ...(selectedReason === "other" && otherComment.trim() ? { cancelComment: otherComment.trim() } : {}),
      });
      trackGaEvent("subscription_cancelled", { cancelReason: selectedReason ?? "none" });
      toast.success("Your subscription has been cancelled.");
      navigate(PageRoutes.AccountPage);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async () => {
    if (!subscriptionId) {
      toast.error("Subscription not found");
      return;
    }
    setActionLoading("resume");
    try {
      const url = APIRoutes.PauseSubscription.replace(":id", subscriptionId);
      await HttpService.post(`${url}?isPause=false`, {});
      trackGaEvent("save_flow_resume");
      toast.success("Subscription resumed. Welcome back!");
      navigate(PageRoutes.AccountPage);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRenew = async () => {
    if (!subscriptionId) {
      toast.error("Subscription not found");
      return;
    }
    setActionLoading("renew");
    try {
      await HttpService.post(APIRoutes.ChangePlanDetails, {
        subscriptionId,
        cancel_at_period_end: false,
      });
      trackGaEvent("save_flow_renew");
      toast.success("Your subscription will continue. Nothing was cancelled.");
      navigate(PageRoutes.AccountPage);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDowngrade = async () => {
    if (!subscriptionId || !subState.customerId) {
      toast.error("Subscription not found");
      return;
    }
    setActionLoading("downgrade");
    try {
      const plansRes = await HttpService.get(APIRoutes.GetPlans);
      const plans = plansRes?.data?.data ?? plansRes?.data ?? [];
      const wantName = summary.interval === "year" ? "Yearly" : "Monthly";
      const basic = (Array.isArray(plans) ? plans : []).find(
        (p: any) => String(p.PlanTypeID) === "1" && p.Name === wantName
      );
      if (!basic) {
        toast.error("Basic plan is not available right now");
        setActionLoading(null);
        return;
      }
      const res = await HttpService.post(APIRoutes.CustomerPortal, {
        priceId: basic.PlanID,
        subscriptionId,
        customerId: subState.customerId,
        planTypeId: 1,
      });
      const data = res?.data?.data ?? res?.data;
      if (data?.clientSecret) {
        toast.info("This change needs extra confirmation. Please use the Plans page.");
        return;
      }
      trackGaEvent(SAVE_FLOW_GA_EVENTS.DOWNGRADE);
      toast.success("Switched to Basic. You'll keep watching for less.");
      navigate(PageRoutes.AccountPage);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setActionLoading(null);
    }
  };

  const subheadText =
    segment === "seasonal"
      ? "You're in the off-season right now. There are no live games until the fall. Here are a few ways to keep your account, your clips, and your price without paying through the summer."
      : "Before you go, here are a few ways to keep your account, saved clips, and locked price, without losing everything.";

  const seasonalMonth = summary.seasonalResumeAt
    ? toDate(summary.seasonalResumeAt).format("MMMM")
    : "the season";

  // Only show a rung the backend marks eligible (e.g. downgrade is Insider-only,
  // seasonal is off-season-only). Default to visible if the flag is absent.
  const offerEligible = (type: string) => {
    const o = offers.find((x) => x.type === type);
    return o ? o.eligible : true;
  };
  const isPaused = subState.isPaused;
  const isCanceled = subState.isCanceled;
  // When the sub is already paused or set to cancel, the only sensible action is
  // resume/renew first. Disable every offer rung (pause, seasonal, downgrade) so
  // the user can't stack a conflicting change on top; only "Keep my subscription"
  // stays live.
  const offersDisabled = isPaused || isCanceled;
  const statusBanner = isPaused
    ? subState.pauseResumesAt
      ? `Your subscription is held until ${toDate(subState.pauseResumesAt).format(
          "MMMM D, YYYY"
        )}. Billing is paused and your price is locked.`
      : "Your subscription is paused. No charges until you resume."
    : isCanceled
    ? `Your subscription is set to cancel${
        subState.periodEnd
          ? ` on ${toDate(subState.periodEnd).format("MMMM D, YYYY")}`
          : " at the end of the period"
      }. You keep access until then.`
    : null;

  const headerTitle = isPaused
    ? "Your subscription is paused"
    : isCanceled
    ? "Your subscription is ending"
    : "Before you go…";
  const headerSubhead = isPaused
    ? "No charges while you're paused. Your account, saved clips, and locked price are all kept. Resume whenever you're ready."
    : isCanceled
    ? `You'll keep access${
        subState.periodEnd
          ? ` until ${toDate(subState.periodEnd).format("MMMM D, YYYY")}`
          : ""
      }. Changed your mind? You can renew anytime before then.`
    : subheadText;
  const cardLoadingKey = isPaused ? "resume" : isCanceled ? "renew" : "generic";
  const cardCtaLabel = isPaused
    ? "Resume subscription"
    : isCanceled
    ? "Keep my subscription"
    : "Pause and keep my account";
  const cardCtaAction = isPaused
    ? handleResume
    : isCanceled
    ? handleRenew
    : () => handlePause("generic");
  const cardNote = isPaused
    ? "Your subscription is paused. Resume in one tap to pick up right where you left off. Your price stays locked."
    : isCanceled
    ? "Your subscription will end at the close of the period. Renew anytime before then to keep uninterrupted access."
    : "Pausing keeps your account, saved clips, personalized spotlight, and current price. Nothing is charged while paused, and you can resume in one tap anytime.";

  // HTPTV-695: subStatusLoading joins the page's own fetch here so the blocked
  // card below never flashes over a healthy flow while the shared status fetch
  // is still in flight. accountLoading joins for the same reason: subscriptionId
  // (read by the gate below) comes from fetchAccountDetails, which resolves
  // independently of fetchOffers and can still be in flight after `loading`
  // clears.
  if (loading || subStatusLoading || accountLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // A subscription that never started has nothing to pause or cancel, and every
  // action below needs a subscription id it would not have. Requiring both
  // sources to be empty for the no-subscription case keeps a failed status fetch
  // from blocking a healthy subscriber. The two cases get distinct copy: one has
  // a subscription stuck before activation, the other has no subscription at
  // all.
  const neverStarted = NOT_STARTED_STATUSES.includes(
    liveSubscription?.status ?? ""
  );
  const noSubscription = !liveSubscription && !subscriptionId;

  if (neverStarted || noSubscription) {
    return (
      <NothingToCancel
        payUrl={buildPayUrl(pendingPayment)}
        reason={neverStarted ? "never_started" : "no_subscription"}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "action.hover", pb: 8, "& *": { fontStyle: "normal !important" } }}>
      <Box sx={{ p: 4, px: { xs: 2, sm: 4, md: 8 }, pt: { xs: 3, md: 4 } }}>

        {/* Page header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={800} color={primary} sx={{ textTransform: "none", mb: 1 }}>
            {headerTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, textTransform: "none", lineHeight: 1.7 }}>
            {headerSubhead}
          </Typography>
        </Box>

        {/* Current-state banner: paused / held / set-to-cancel + resume/renew */}
        {statusBanner && (
          <Alert
            severity="warning"
            action={
              <Button
                variant="outlined"
                size="small"
                disabled={!!actionLoading}
                onClick={isPaused ? handleResume : handleRenew}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {isPaused
                  ? actionLoading === "resume" ? "Resuming…" : "Resume subscription"
                  : actionLoading === "renew" ? "Renewing…" : "Keep my subscription"}
              </Button>
            }
            sx={{
              mb: 3,
              alignItems: "center",
              "& .MuiAlert-message": {
                fontWeight: 600,
                fontSize: "0.875rem",
              },
            }}
          >
            {statusBanner}
          </Alert>
        )}

        <Grid container spacing={3} alignItems="flex-start">

          {/* LEFT COLUMN: Offer ladder */}
          <Grid item xs={12} md={8}>

            {/* Pause rung: RECOMMENDED */}
            <Box sx={{ position: "relative", mb: 2.5, mt: 1.5 }}>
              {/* RECOMMENDED badge overlapping top-left border */}
              <Box sx={{
                position: "absolute", top: -8, left: 14,
                bgcolor: "error.main", color: tertiary,
                fontSize: "0.65rem", fontWeight: 800,
                px: 1.25, py: 0.4, borderRadius: "6px",
                letterSpacing: 1, textTransform: "uppercase",
                zIndex: 1, lineHeight: 1,
              }}>
                Recommended
              </Box>
              <Paper elevation={0} sx={{
                border: `2px solid ${primary}`,
                borderRadius: "14px", p: 2.5,
                bgcolor: "background.paper",
              }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "flex-start" }}>
                  {/* Custom pause icon: two vertical bars in dark rounded square */}
                  <Box sx={{
                    width: 44, height: 44, borderRadius: "10px",
                    bgcolor: primary, display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0, gap: "5px",
                  }}>
                    <Box sx={{ width: 5, height: 18, bgcolor: tertiary, borderRadius: "2px" }} />
                    <Box sx={{ width: 5, height: 18, bgcolor: tertiary, borderRadius: "2px" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                      <Typography fontWeight={800} variant="subtitle1" sx={{ textTransform: "none" }}>
                        Pause my subscription
                      </Typography>
                      <Chip label="FREE" size="small" sx={{
                        bgcolor: "success.main", color: tertiary,
                        fontWeight: 700, fontSize: "0.65rem", height: 20,
                        textTransform: "none",
                      }} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none", lineHeight: 1.7, mb: 1.5 }}>
                      Stop billing now and pick back up anytime. You keep your account, saved clips, highlights, and personalized spotlight, and you lock in your current price. No charges while you're paused.
                    </Typography>
                    <Button
                      variant="contained"
                      disableElevation
                      disabled={offersDisabled || actionLoading === "generic"}
                      onClick={() => handlePause("generic")}
                      sx={{
                        bgcolor: primary, color: tertiary, textTransform: "none",
                        fontWeight: 700, borderRadius: "8px", px: 3,
                        "&:hover": { bgcolor: primary, opacity: 0.88 },
                      }}
                    >
                      {actionLoading === "generic" ? <CircularProgress size={18} sx={{ color: tertiary }} /> : "Pause for free"}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Box>

            {/* Seasonal hold rung (off-season only) */}
            {offerEligible("seasonal") && (
            <Paper elevation={0} sx={{
              border: "1px solid", borderColor: "divider",
              borderRadius: "14px", p: 2.5, mb: 2.5,
              bgcolor: "background.paper",
            }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                <Box sx={{
                  width: 44, height: 44, borderRadius: "10px",
                  bgcolor: "success.light", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <AlarmIcon sx={{ color: "success.dark", fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={800} variant="subtitle1" sx={{ textTransform: "none", mb: 0.5 }}>
                    See you in {seasonalMonth}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none", lineHeight: 1.7, mb: 1.5 }}>
                    Taking the off-season off? We'll pause your billing now and send you a one-tap reactivation the moment the new season starts. Your price stays locked until then.
                  </Typography>
                  <Button
                    variant="outlined"
                    disabled={offersDisabled || actionLoading === "seasonal"}
                    onClick={() => handlePause("seasonal")}
                    sx={{
                      borderColor: primary, color: primary, textTransform: "none",
                      fontWeight: 700, borderRadius: "8px", px: 3,
                      "&:hover": { borderColor: primary, color: primary, bgcolor: `${primary}08` },
                    }}
                  >
                    {actionLoading === "seasonal" ? <CircularProgress size={18} /> : "Hold until the season"}
                  </Button>
                </Box>
              </Stack>
            </Paper>
            )}

            {/* Downgrade rung (Insider only) */}
            {offerEligible("downgrade") && (
            <Paper elevation={0} sx={{
              border: "1px solid", borderColor: "divider",
              borderRadius: "14px", p: 2.5, mb: 2.5,
              bgcolor: "background.paper",
            }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                <Box sx={{
                  width: 44, height: 44, borderRadius: "10px",
                  bgcolor: "action.selected", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                  border: "1px solid", borderColor: "divider",
                }}>
                  <ArrowDownwardIcon sx={{ color: "text.secondary", fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={800} variant="subtitle1" sx={{ textTransform: "none", mb: 0.5 }}>
                    Switch to Basic instead
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none", lineHeight: 1.7, mb: 1.5 }}>
                    Keep watching everything live and on-demand at a lower price. Drop the extras (personalized spotlights, watch parties, and clip downloads) and stay subscribed for less.
                  </Typography>
                  <Button
                    variant="outlined"
                    disabled={!!actionLoading || offersDisabled}
                    onClick={handleDowngrade}
                    sx={{
                      borderColor: primary, color: primary, textTransform: "none",
                      fontWeight: 700, borderRadius: "8px", px: 3,
                      "&:hover": { borderColor: primary, color: primary, bgcolor: `${primary}08` },
                    }}
                  >
                    {actionLoading === "downgrade" ? <CircularProgress size={18} /> : "Switch to Basic"}
                  </Button>
                </Box>
              </Stack>
            </Paper>
            )}

            {/* Cancel reason chips + cancel-anyway */}
            <Paper elevation={0} sx={{
              border: "1px solid", borderColor: "divider",
              borderRadius: "14px", p: 2.5,
              bgcolor: "background.paper",
            }}>
              {/* Reason prompt only makes sense before cancelling; hide it once
                  the sub is already set to cancel (only renew remains). */}
              {!isCanceled && (
                <>
                  <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ textTransform: "none" }}>
                    If you do cancel, mind sharing why? (optional)
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} mb={1.5}>
                    {(Object.entries(CANCEL_REASONS) as [string, CancelReasonValue][]).map(([, value]) => (
                      <Chip
                        key={value}
                        label={REASON_LABELS[value]}
                        variant={selectedReason === value ? "filled" : "outlined"}
                        onClick={() => {
                          setSelectedReason(selectedReason === value ? null : value);
                          if (value !== "other") setOtherComment("");
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: selectedReason === value ? 700 : 400,
                          borderColor: selectedReason === value ? primary : "divider",
                          bgcolor: selectedReason === value ? `${primary}14` : "transparent",
                          color: selectedReason === value ? primary : "text.secondary",
                          "&:hover": { borderColor: primary, color: primary },
                        }}
                      />
                    ))}
                  </Stack>
                  <Collapse in={selectedReason === "other"} unmountOnExit>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Tell us more (optional)"
                      value={otherComment}
                      onChange={(e) => setOtherComment(e.target.value)}
                      inputProps={{ maxLength: 300 }}
                      sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.875rem" } }}
                    />
                  </Collapse>
                </>
              )}
              <Box sx={{ textAlign: "center" }}>
                {isCanceled ? (
                  <Link
                    component="button"
                    underline="always"
                    color="primary"
                    onClick={handleRenew}
                    disabled={!!actionLoading}
                    sx={{ fontSize: "0.875rem", textTransform: "none", cursor: "pointer", fontWeight: 700 }}
                  >
                    {actionLoading === "renew"
                      ? "Renewing…"
                      : "Keep my subscription"}
                  </Link>
                ) : (
                  <Link
                    component="button"
                    underline="always"
                    color="text.secondary"
                    onClick={handleCancel}
                    disabled={actionLoading === "cancel"}
                    sx={{ fontSize: "0.875rem", textTransform: "none", cursor: "pointer" }}
                  >
                    {actionLoading === "cancel"
                      ? "Cancelling…"
                      : "No thanks, cancel my subscription"}
                  </Link>
                )}
              </Box>
            </Paper>

          </Grid>

          {/* RIGHT COLUMN: Your Subscription summary card */}
          <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }}>
            <Box sx={{ position: "sticky", top: 88 }}>
              <Paper elevation={2} sx={{ borderRadius: "16px", overflow: "hidden" }}>

                {/* Header */}
                <Box sx={{ bgcolor: primary, px: 3, py: 2 }}>
                  <Typography variant="h6" fontWeight={700} color={tertiary} textAlign="center" sx={{ textTransform: "none" }}>
                    Your Subscription
                  </Typography>
                </Box>

                <Box sx={{ px: 2.5, py: 2.5 }}>
                  {/* Plan preview card */}
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: 1.5, mb: 2.5, border: "1px solid", borderColor: "divider",
                    borderRadius: "10px",
                  }}>
                    <img
                      src={configData?.logoUrl}
                      alt={configData?.personalChannel}
                      style={{ height: 32, objectFit: "contain", maxWidth: 60 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ textTransform: "none" }}>
                        {summary.planLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "none" }}>
                        {summary.price && `${fmtPrice(summary.price)} / ${summary.interval}`}
                        {summary.renewsAt && ` · renews ${toDate(summary.renewsAt).format("MMM D")}`}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Line items */}
                  {summary.memberSince && (
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>Member since</Typography>
                      <Typography variant="body2" sx={{ textTransform: "none" }}>
                        {dayjs(summary.memberSince).format("MMM YYYY")}
                      </Typography>
                    </Stack>
                  )}
                  {summary.savedClipsCount !== null && (
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>Saved clips & highlights</Typography>
                      <Typography variant="body2" sx={{ textTransform: "none" }}>{summary.savedClipsCount}</Typography>
                    </Stack>
                  )}
                  {summary.personalizedChannelActive !== null && (
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>Personalized spotlight</Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600} sx={{ textTransform: "none" }}>
                        {summary.personalizedChannelActive ? "Active" : "Inactive"}
                      </Typography>
                    </Stack>
                  )}
                  {!isPaused && !isCanceled && (
                    <>
                      <Stack direction="row" justifyContent="space-between" mb={2}>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: "none" }}>If you pause</Typography>
                        <Typography variant="body2" sx={{ textTransform: "none" }}>$0.00 / month</Typography>
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: "none" }}>Keep everything for</Typography>
                        <Typography fontWeight={800} color={primary} sx={{ fontSize: "1.1rem", textTransform: "none" }}>$0 today</Typography>
                      </Stack>
                    </>
                  )}

                  {/* State-aware CTA: Pause (active) / Resume (paused) / Renew (canceled) */}
                  <Button
                    fullWidth variant="contained" disableElevation
                    disabled={!!actionLoading}
                    onClick={cardCtaAction}
                    sx={{
                      bgcolor: primary, color: tertiary, textTransform: "none",
                      fontWeight: 700, borderRadius: "8px", py: 1.25, mb: 1.5, mt: isPaused || isCanceled ? 1 : 0,
                      "&:hover": { bgcolor: primary, opacity: 0.9 },
                    }}
                  >
                    {actionLoading === cardLoadingKey
                      ? <CircularProgress size={18} sx={{ color: tertiary }} />
                      : cardCtaLabel}
                  </Button>

                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.6} sx={{ textTransform: "none" }}>
                    {cardNote}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>

        </Grid>
      </Box>
    </Box>
  );
};

export default CancelSaveFlow;
